import { api } from '@packages/backend/convex/_generated/api';
import {
  BRAND_KEYS,
  BRANDS,
  FEATURE_FLAG_KEYS,
  FEATURE_FLAG_TYPE_LABELS,
  FEATURE_FLAG_TYPES,
  FEATURE_FLAGS,
  type FeatureFlagCvx,
  type FeatureFlagKey,
  getRestaurantPlanBrand,
  getRestaurantPlanChangeFeatureFlagKeys,
  getRestaurantPlanFeatureFlagKeys,
  getUserErrorMessage,
  RESTAURANT_PLAN_KEYS,
  RESTAURANT_PLANS,
  type RestaurantPlanCvx,
  type RestaurantPlanKey,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import { ColorBadge } from '@/components/ColorBadge';
import { FormWrapper } from '@/components/FormWrapper';
import { LoadingIcon } from '@/components/LoadingIcon';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { FormField } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { SuperAdminOnly } from '@/features/auth/SuperAdminOnly';
import { cn } from '@/lib/cn';
import { log } from '@/lib/logging';

/**
 * One switch per flag, each its own field: on a Convex sync, unsaved switches keep their values while
 * the rest follow Convex. Top-level, not nested under one object: `keepDirtyValues` keeps a dirty
 * object whole, so collapsed (never mounted) switches inside it would stop following Convex.
 */
type FormValues = Partial<Record<FeatureFlagKey, boolean>>;

/** Each registry flag's switch: on when the venue has its row. */
const getFormValues = (featureFlags: FeatureFlagCvx[]): FormValues =>
  Object.fromEntries(
    FEATURE_FLAG_KEYS.map((key) => [
      key,
      featureFlags.some((featureFlag) => featureFlag.key === key),
    ]),
  );

/** The plan picker's value for "No plan": Radix Select items can't have an empty value. */
const NO_PLAN = 'no-plan';

export const Route = createFileRoute('/r/$restaurantId/s/feature-flags')({
  component: FeatureFlagsPage,
});

/**
 * Super admins pick this venue's plan, and turn its feature flags on or off then save them
 * together. Venue staff change their plan's features on the Features page instead.
 */
function FeatureFlagsPage() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const { data: featureFlags, isPending } = useQuery(api.featureFlags.listByRestaurantId, {
    restaurantId,
  });
  const restaurantPlanQuery = useQuery(api.restaurantPlans.getByRestaurantId, { restaurantId });

  const form = useForm<FormValues>({ defaultValues: getFormValues([]) });
  // Flags with a `turnOffWarning` that the save waiting for a yes turns off
  const [warnedKeys, setWarnedKeys] = useState<FeatureFlagKey[]>([]);

  // Subscribe to changes
  useEffect(() => {
    if (!featureFlags) return;
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(getFormValues(featureFlags), { keepDirty: true, keepDirtyValues: true });
  }, [featureFlags]);

  const onSubmit = async (values: FormValues) => {
    // Only the switches that differ from Convex: saving the others could undo a change made
    // elsewhere since. Hoisted out of the `try`: React Compiler can't lower a conditional inside one.
    const changedKeys = FEATURE_FLAG_KEYS.filter(
      (key) => values[key] !== form.formState.defaultValues?.[key],
    );
    const enabledKeys = changedKeys.filter((key) => values[key] === true);
    try {
      await convex.mutation(api.featureFlags.save, {
        restaurantId,
        keys: changedKeys,
        enabledKeys,
      });
      for (const key of changedKeys) {
        log.info('Feature flag changed', {
          area: 'feature_flags',
          feature_flag_key: key,
          is_enabled: enabledKeys.includes(key),
        });
      }
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      toast.success('Saved successfully');
    } catch (error) {
      toast.error("Couldn't save", { description: getUserErrorMessage(error) });
    }
  };

  /** A save that turns off a flag with a `turnOffWarning` waits for a yes. */
  const onSave = async (values: FormValues) => {
    const turningOffWarnedKeys = FEATURE_FLAG_KEYS.filter(
      (key) =>
        FEATURE_FLAGS[key].turnOffWarning !== null &&
        values[key] === false &&
        form.formState.defaultValues?.[key] === true,
    );
    if (turningOffWarnedKeys.length > 0) {
      setWarnedKeys(turningOffWarnedKeys);
      return;
    }
    await onSubmit(values);
  };

  if (isPending || restaurantPlanQuery.isPending) return <LoadingIcon />;
  if (!featureFlags || restaurantPlanQuery.isError) return null; // Handled by parent component

  const restaurantPlan = restaurantPlanQuery.data;
  const planFeatureFlagKeys = getRestaurantPlanFeatureFlagKeys(restaurantPlan?.planKey ?? null);

  return (
    <SuperAdminOnly>
      {/* Keyed by the plan, so a plan change resets the picker */}
      <PlanCard
        key={[
          restaurantPlan?.brand,
          restaurantPlan?.planKey ?? NO_PLAN,
          restaurantPlan?.stripeSubscriptionId,
        ].join('-')}
        restaurantId={restaurantId}
        restaurantPlan={restaurantPlan}
        featureFlags={featureFlags}
        hasUnsavedFlags={form.formState.isDirty}
      />
      <Card>
        <CardHeader>
          <CardTitle>Feature flags</CardTitle>
        </CardHeader>
        <CardContent>
          <FormWrapper {...form}>
            <form id="form" onSubmit={form.handleSubmit(onSave)} className="grid gap-6">
              {FEATURE_FLAG_TYPES.map((type) => (
                // Experimental flags are rarely touched, so they start collapsed
                <Collapsible
                  key={type}
                  className="grid gap-3"
                  defaultOpen={type !== 'experimental'}
                >
                  <h3 className="font-semibold">
                    {type === 'experimental' ? (
                      <CollapsibleTrigger className="group flex cursor-pointer items-center gap-2">
                        <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
                        {FEATURE_FLAG_TYPE_LABELS[type]}
                      </CollapsibleTrigger>
                    ) : (
                      FEATURE_FLAG_TYPE_LABELS[type]
                    )}
                  </h3>
                  <CollapsibleContent className="grid gap-3">
                    {FEATURE_FLAG_KEYS.filter((key) => FEATURE_FLAGS[key].type === type).map(
                      (key) => (
                        <div
                          key={key}
                          className={cn(
                            'flex items-center justify-between gap-4 rounded-lg border p-4',
                            // Flags outside the venue's plan are hidden from its staff, so they get the super admin orange
                            !planFeatureFlagKeys.includes(key) &&
                              'border-orange-300 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/40',
                          )}
                        >
                          <label className="grid gap-1" htmlFor={`feature-flag-${key}`}>
                            <span className="flex items-center gap-2 font-medium">
                              {FEATURE_FLAGS[key].label}
                              {planFeatureFlagKeys.includes(key) && (
                                <ColorBadge color="green">In plan</ColorBadge>
                              )}
                            </span>
                            <span className="text-muted-foreground text-sm">
                              {FEATURE_FLAGS[key].description}
                            </span>
                          </label>
                          <FormField
                            control={form.control}
                            name={key}
                            render={({ field }) => (
                              <Switch
                                id={`feature-flag-${key}`}
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            )}
                          />
                        </div>
                      ),
                    )}
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </form>
          </FormWrapper>
        </CardContent>
        <CardFooter className="border-t">
          <ActionButton
            disabled={form.formState.isSubmitting || !form.formState.isDirty}
            loading={form.formState.isSubmitting}
            type="submit"
            form="form"
          >
            Save
          </ActionButton>
        </CardFooter>
      </Card>
      <TurnOffWarningDialog
        featureFlagKeys={warnedKeys}
        isSaving={form.formState.isSubmitting}
        onCancel={() => setWarnedKeys([])}
        onConfirm={form.handleSubmit(async (values) => {
          await onSubmit(values);
          setWarnedKeys([]);
        })}
      />
    </SuperAdminOnly>
  );
}

/**
 * Asks before a save turns off `featureFlagKeys`, listing each flag's `turnOffWarning`. Open while
 * `featureFlagKeys` holds any.
 */
const TurnOffWarningDialog = ({
  featureFlagKeys,
  isSaving,
  onCancel,
  onConfirm,
}: {
  featureFlagKeys: FeatureFlagKey[];
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <AlertDialog
    open={featureFlagKeys.length > 0}
    onOpenChange={(isOpen) => {
      if (!isOpen) onCancel();
    }}
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Turn off anyway?</AlertDialogTitle>
        <AlertDialogDescription className="grid gap-2">
          {/* Spans, not paragraphs: the description renders as a <p> */}
          {featureFlagKeys.map((key) => (
            <span key={key}>
              {FEATURE_FLAGS[key].label}: {FEATURE_FLAGS[key].turnOffWarning}
            </span>
          ))}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <ActionButton loading={isSaving} onClick={onConfirm} variant="destructive">
          Turn off
        </ActionButton>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

/**
 * The venue's brand and plan. It starts on the venue's plan, or on its brand's default plan without
 * one, and picking another brand starts on that brand's default plan. Changing the plan turns on
 * the flags the new plan adds and turns off the flags it drops. It waits for unsaved flag changes,
 * since saving them afterwards would undo the change. A subscribed venue's plan comes from its
 * subscription, so it can't be changed here.
 */
const PlanCard = ({
  restaurantId,
  restaurantPlan,
  featureFlags,
  hasUnsavedFlags,
}: {
  restaurantId: string;
  restaurantPlan: RestaurantPlanCvx | null;
  featureFlags: FeatureFlagCvx[];
  hasUnsavedFlags: boolean;
}) => {
  const convex = useConvex();
  const isSubscribed = restaurantPlan !== null && restaurantPlan.stripeSubscriptionId !== null;
  const brand = getRestaurantPlanBrand(restaurantPlan);
  const planKey = restaurantPlan?.planKey ?? null;
  const [selectedBrand, setSelectedBrand] = useState(brand);
  const [selectedPlanKey, setSelectedPlanKey] = useState<RestaurantPlanKey | null>(
    planKey ?? BRANDS[brand].defaultPlanKey,
  );
  const hasChanges = selectedBrand !== brand || selectedPlanKey !== planKey;

  const { turnedOnKeys, turnedOffKeys } = getRestaurantPlanChangeFeatureFlagKeys(
    planKey,
    selectedPlanKey,
  );
  // Only what actually changes: a flag already on doesn't turn on, one already off doesn't turn off
  const isEnabled = (key: FeatureFlagKey) =>
    featureFlags.some((featureFlag) => featureFlag.key === key);
  const turningOnLabels = turnedOnKeys
    .filter((key) => !isEnabled(key))
    .map((key) => FEATURE_FLAGS[key].label);
  const turningOffLabels = turnedOffKeys
    .filter((key) => isEnabled(key))
    .map((key) => FEATURE_FLAGS[key].label);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isChangingPlan, setIsChangingPlan] = useState(false);

  const changePlan = async () => {
    setIsChangingPlan(true);
    try {
      await convex.mutation(api.restaurantPlans.set, {
        restaurantId,
        brand: selectedBrand,
        planKey: selectedPlanKey,
      });
    } catch (error) {
      toast.error("Couldn't change plan", { description: getUserErrorMessage(error) });
      setIsChangingPlan(false);
      return;
    }
    log.info('Plan changed', {
      area: 'restaurant_plans',
      brand: selectedBrand,
      plan_key: selectedPlanKey,
      previous_plan_key: planKey,
    });
    setIsChangingPlan(false);
    setIsConfirmOpen(false);
    toast.success('Plan changed');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Plan</CardTitle>
        <CardDescription>
          {isSubscribed
            ? "This venue's plan comes from its Stripe subscription."
            : 'Changing the plan turns on the flags the new plan adds and turns off the flags it drops.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        <p className="text-muted-foreground text-sm">
          Current plan: {planKey === null ? 'No plan' : RESTAURANT_PLANS[planKey].label}
        </p>
        <Select
          disabled={isSubscribed}
          value={selectedBrand}
          onValueChange={(value) => {
            const newBrand = BRAND_KEYS.find((key) => key === value);
            if (!newBrand) return;
            setSelectedBrand(newBrand);
            setSelectedPlanKey(BRANDS[newBrand].defaultPlanKey);
          }}
        >
          <SelectTrigger aria-label="Brand" className="w-[280px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BRAND_KEYS.map((key) => (
              <SelectItem key={key} value={key}>
                {BRANDS[key].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          disabled={isSubscribed}
          value={selectedPlanKey ?? NO_PLAN}
          onValueChange={(value) =>
            setSelectedPlanKey(RESTAURANT_PLAN_KEYS.find((key) => key === value) ?? null)
          }
        >
          <SelectTrigger aria-label="Plan" className="w-[280px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_PLAN}>No plan</SelectItem>
            {RESTAURANT_PLAN_KEYS.filter(
              (key) => RESTAURANT_PLANS[key].brand === selectedBrand,
            ).map((key) => (
              <SelectItem key={key} value={key}>
                {RESTAURANT_PLANS[key].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasUnsavedFlags && (
          <p className="text-muted-foreground text-sm">
            Save or undo your flag changes before changing the plan.
          </p>
        )}
      </CardContent>
      <CardFooter className="border-t">
        <ActionButton
          disabled={isSubscribed || !hasChanges || hasUnsavedFlags}
          onClick={() => setIsConfirmOpen(true)}
        >
          Change plan
        </ActionButton>
      </CardFooter>
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change this venue's plan?</AlertDialogTitle>
            <AlertDialogDescription className="grid gap-2">
              {/* Spans, not paragraphs: the description renders as a <p> */}
              {turningOnLabels.length > 0 && <span>Turns on: {turningOnLabels.join(', ')}</span>}
              {turningOffLabels.length > 0 && <span>Turns off: {turningOffLabels.join(', ')}</span>}
              {turningOnLabels.length === 0 && turningOffLabels.length === 0 && (
                <span>No flags turn on or off.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <ActionButton loading={isChangingPlan} onClick={changePlan}>
              Yes
            </ActionButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
